import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { NativeSelect } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { periodLabel, rupiah } from "@/lib/format";

const STATUS = {
  belum_bayar: ["BELUM BAYAR", "bg-rose-100 text-rose-800"],
  sebagian: ["HUTANG SEBAGIAN", "bg-amber-100 text-amber-800"],
  lunas: ["LUNAS", "bg-emerald-100 text-emerald-800"],
  lebih_bayar: ["LEBIH BAYAR", "bg-sky-100 text-sky-800"],
};

export const DebtBadge = ({ status, testId }) => {
  const s = STATUS[status];
  if (!s) return <span className="text-slate-400 text-xs">-</span>;
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm whitespace-nowrap ${s[1]}`} data-testid={testId}>{s[0]}</span>;
};

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function PaymentDialog({ target, onClose, onDone }) {
  const { can } = useAuth();
  const partial = can("payment:partial");
  const [f, setF] = useState({ amount: "", date: today(), method: "tunai", note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (target) setF({ amount: String(target.remaining), date: today(), method: "tunai", note: "" });
  }, [target]);
  if (!target) return null;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (amount) => {
    setBusy(true);
    try {
      await api.post("/payments", { customer_id: target.customer_id, period: target.period, amount: Number(amount), date: f.date, method: f.method, note: f.note });
      toast.success(`Pembayaran ${rupiah(amount)} dari ${target.customer_name} tercatat`);
      onClose();
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Catat Pembayaran</DialogTitle>
          <DialogDescription>{target.customer_name} · Periode {periodLabel(target.period)}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="border rounded-sm p-2"><div className="text-slate-500">Tagihan</div><div className="font-mono font-semibold">{rupiah(target.due)}</div></div>
          <div className="border rounded-sm p-2"><div className="text-slate-500">Dibayar</div><div className="font-mono font-semibold">{rupiah(target.paid)}</div></div>
          <div className="border border-rose-300 bg-rose-50 rounded-sm p-2"><div className="text-rose-700">Sisa Hutang</div><div className="font-mono font-semibold text-rose-800" data-testid="payment-remaining">{rupiah(target.remaining)}</div></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Jumlah Bayar (Rp)</Label><Input type="number" min={1} max={target.remaining} value={f.amount} onChange={set("amount")} readOnly={!partial} className="font-mono" data-testid="payment-amount-input" /></div>
          <div><Label className="text-xs">Tanggal</Label><Input type="date" value={f.date} onChange={set("date")} data-testid="payment-date-input" /></div>
          <div><Label className="text-xs">Metode</Label>
            <NativeSelect value={f.method} onChange={set("method")} className="w-full" data-testid="payment-method-select">
              <option value="tunai">Tunai</option><option value="transfer">Transfer</option><option value="lainnya">Lainnya</option>
            </NativeSelect></div>
          <div><Label className="text-xs">Catatan</Label><Input value={f.note} onChange={set("note")} placeholder="opsional" data-testid="payment-note-input" /></div>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {partial && <Button variant="outline" disabled={busy || !(Number(f.amount) > 0)} onClick={() => submit(f.amount)} data-testid="payment-submit-btn">Catat Bayar Sebagian</Button>}
          <Button disabled={busy} onClick={() => submit(target.remaining)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="payment-pay-full-btn">Lunasi {rupiah(target.remaining)}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
